import React, { useState, useCallback, useMemo } from 'react';
import { CurrencyRupeeIcon, PlusIcon, PencilIcon, TrashIcon, XMarkIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';

// ====================================================================
// Mock Data Store & Constants
// ====================================================================

const initialHeads = [
    {
        id: 'EDH001',
        name: 'Basic Salary',
        type: 'Earning',
        category: 'Statutory',
        calculation: 'Percentage of CTC (50%)',
        taxable: true,
    },
    {
        id: 'EDH002',
        name: 'House Rent Allowance (HRA)',
        type: 'Earning',
        category: 'Reimbursement',
        calculation: 'Fixed Amount',
        taxable: 'Partially Tax Exempt',
    },
    {
        id: 'EDH003',
        name: 'Professional Tax (PT)',
        type: 'Deduction',
        category: 'Statutory',
        calculation: 'Fixed Slab',
        taxable: false,
    },
    {
        id: 'EDH004',
        name: 'TDS (Income Tax)',
        type: 'Deduction',
        category: 'Statutory',
        calculation: 'Tax Calculator',
        taxable: false,
    },
];

const typeOptions = ['Earning', 'Deduction'];
const categoryOptions = ['Statutory', 'Allowance', 'Reimbursement', 'Voluntary'];
const calculationOptions = ['Fixed Amount', 'Percentage of CTC', 'Percentage of Basic', 'Fixed Slab', 'Tax Calculator'];

// ====================================================================
// HeadFormModal (Nested)
// ====================================================================

const HeadFormModal = ({ isOpen, onClose, onSave, head }) => {
    const isNew = useMemo(() => head === null, [head]);
    const [headData, setHeadData] = useState(() => ({
        id: head?.id || null,
        name: head?.name || '',
        type: head?.type || typeOptions[0],
        category: head?.category || categoryOptions[0],
        calculation: head?.calculation || calculationOptions[0],
        taxable: head?.taxable ?? true,
    }));

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setHeadData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!headData.name.trim()) {
            alert('Please provide an Earning/Deduction Head name.');
            return;
        }
        onSave(headData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel 
                                className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-xl font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Create New Payroll Head' : `Edit Head: ${headData.name}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div>
                                        <label htmlFor="name" className="block text-xs font-medium text-gray-700">Head Name (e.g., Conveyance Allowance)</label>
                                        <input
                                            type="text"
                                            name="name"
                                            id="name"
                                            value={headData.name}
                                            onChange={handleInputChange}
                                            required
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label htmlFor="type" className="block text-xs font-medium text-gray-700">Type</label>
                                            <select
                                                name="type"
                                                id="type"
                                                value={headData.type}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="category" className="block text-xs font-medium text-gray-700">Category</label>
                                            <select
                                                name="category"
                                                id="category"
                                                value={headData.category}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                {categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="calculation" className="block text-xs font-medium text-gray-700">Calculation Method</label>
                                            <select
                                                name="calculation"
                                                id="calculation"
                                                value={headData.calculation}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                {calculationOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="pt-2">
                                        <label htmlFor="taxable" className="block text-xs font-medium text-gray-700 mb-2">Taxability Status</label>
                                        <select
                                            name="taxable"
                                            id="taxable"
                                            value={headData.taxable}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                        >
                                            <option value={true}>Fully Taxable</option>
                                            <option value={'Partially Tax Exempt'}>Partially Tax Exempt (e.g., HRA)</option>
                                            <option value={false}>Non-Taxable (e.g., Deductions)</option>
                                        </select>
                                    </div>

                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">Cancel</button>
                                        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700">
                                            {isNew ? 'Create Head' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// ====================================================================
// EarningDeductionHeadSetup (Main Component)
// ====================================================================

const EarningDeductionHeadSetup = () => {
    const [heads, setHeads] = useState(initialHeads);
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentHead, setCurrentHead] = useState(null);

    // --- CRUD Handlers ---
    const handleCreate = useCallback(() => {
        setCurrentHead(null);
        setIsModalOpen(true);
    }, []);

    const handleEdit = useCallback((head) => {
        setCurrentHead(head);
        setIsModalOpen(true);
    }, []);

    const handleSave = useCallback((newHeadData, isNew) => {
        if (isNew) {
            setHeads(prev => [...prev, { 
                ...newHeadData, 
                id: `EDH${String(prev.length + 1).padStart(3, '0')}`
            }]);
        } else {
            setHeads(prev => prev.map(h => h.id === newHeadData.id ? newHeadData : h));
        }
        setIsModalOpen(false);
    }, []);

    const handleDelete = useCallback((id) => {
        if (window.confirm('Are you sure you want to delete this Payroll Head? This may break existing Salary Structures.')) {
            setHeads(prev => prev.filter(h => h.id !== id));
        }
    }, []);

    const typeBadge = (type) => {
        const color = type === 'Earning' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
        return <span className={`px-3 py-1 text-xs font-semibold rounded-full ${color}`}>{type}</span>;
    };

    return (
        <div 
            className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50"
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            <header className="flex justify-between items-center pb-6 mb-8 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <AdjustmentsHorizontalIcon className="w-8 h-8 inline-block mr-2 text-indigo-600"/> Earning/Deduction Head Setup
                </h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition"
                >
                    <PlusIcon className="w-5 h-5" />
                    <span>New Payroll Head</span>
                </button>
            </header>

            <div className="max-w-6xl mx-auto">
                <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Head Name', 'Type', 'Category', 'Calculation Logic', 'Taxability', 'Actions'].map(header => (
                                    <th
                                        key={header}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style={{ fontFamily: 'Roboto, sans-serif' }}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {heads.map((head) => (
                                <tr key={head.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{head.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{typeBadge(head.type)}</td>
                                    <td className="px-6 py-4 text-indigo-600 font-medium">{head.category}</td>
                                    <td className="px-6 py-4 text-gray-700">{head.calculation}</td>
                                    <td className="px-6 py-4 text-gray-500">
                                        {typeof head.taxable === 'boolean' 
                                            ? (head.taxable ? 'Fully Taxable' : 'Non-Taxable') 
                                            : head.taxable}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                        <button onClick={() => handleEdit(head)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDelete(head.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {heads.length === 0 && (
                                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500 italic">No payroll heads defined.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Head Modal */}
            {isModalOpen && (
                <HeadFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                    head={currentHead}
                />
            )}
        </div>
    );
};

export default EarningDeductionHeadSetup;